import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'

/* The leg page's ONE save (S-10, A3): the state on the left, the «Ставки
 * детализируют» question in the middle, «Отмена» and one blue primary button on
 * the right — «Создать плечо» on `legs/new`, «Сохранить» after.
 *
 * Sticky to the bottom of the page's scroll box and mounted once, by a page
 * that stays mounted across the create's `new` → id switch, so the bar is in
 * the same place before and after the create: the label changes, the bar does
 * not move.
 *
 * A refusal the reader can act on is `blockedReason`, never `disabled` — the
 * reason is reachable by hover, focus and a screen reader. `disabled` is kept
 * for the press in flight, as a separate prop. When the refusal is a missing
 * required field, the bar also offers the way to it (`fix`).
 */
export function LegSaveBar({
  state,
  middle,
  primaryLabel,
  onPrimary,
  blockedReason,
  pending,
  onCancel,
  confirmCancel,
  fix,
}: {
  /** «Несохранённое плечо», «Несохранённые изменения», «Сохраняем…». */
  state: string
  /** The «Ставки детализируют» question, when there is one to ask. */
  middle?: ReactNode
  primaryLabel: string
  onPrimary: () => void
  blockedReason?: string
  pending: boolean
  onCancel: () => void
  /** Cancelling would discard typed work: ask once, in the bar. */
  confirmCancel: boolean
  /** The way to the missing field the primary is refused for. */
  fix?: { label: string; onClick: () => void }
}) {
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState(false)

  return (
    <div
      className={cn(
        'bg-surface border-border-subtle sticky bottom-0 z-20 -mx-4 -mb-12 border-t px-4 py-3 sm:-mx-6 sm:px-6',
        'flex flex-wrap items-center gap-x-4 gap-y-2',
      )}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-text-muted text-[13px] font-light">{state}</span>
        {fix ? (
          <button
            type="button"
            onClick={fix.onClick}
            className="text-text-link hover:text-text-link-hover cursor-pointer self-start text-left text-[12.5px]"
          >
            {fix.label}
          </button>
        ) : null}
      </div>

      {middle ? <div className="flex min-w-0 flex-1 items-center justify-center">{middle}</div> : <div className="flex-1" />}

      <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">
        {confirming ? (
          <>
            <span className="text-text-body text-[13px]">{t('cases.legSave.discardConfirm')}</span>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              {t('cases.legParams.keepEditing')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setConfirming(false)
                onCancel()
              }}
            >
              {t('cases.legParams.discard')}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              disabled={pending}
              onClick={() => (confirmCancel ? setConfirming(true) : onCancel())}
            >
              {t('common.actions.cancel')}
            </Button>
            <Button disabled={pending} blockedReason={blockedReason} onClick={onPrimary}>
              {pending ? t('common.form.saving') : primaryLabel}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
